// The compose panel for a one-off email, on a signup's profile, on a tenant
// page's Email tab, in a Growth lead's Email (pages/growth/LeadEmail.tsx) and in
// an inquiry's Reply (pages/growth/InquiryReply.tsx): template, signer, editable
// subject and body, and a live preview from the server (exactly what would go).
// The caller says who it goes to and which routes preview and send it
// (`OutreachTarget`); everything else is the same for all four. A lead's target
// also offers Schedule (`canSchedule`): a date and a time, sent by the server at
// that time and shown on Growth › Schedule until then. A reply starts from its
// own subject and a blank body (`start`) instead of the first template.
//
// THE OWNER PRESSING SEND (OR SCHEDULE) IS THE APPROVAL. Only the dialog here
// sends `confirm: true`, and it asks once more, naming the address (and the time),
// and says plainly when sending is off (the send is then recorded as a dry run and
// nothing leaves). Each press carries a fresh request_id, so a double click is one
// email. Nothing here sends on its own.

import { useEffect, useMemo, useState, type ReactNode } from 'react'
import { Link } from 'react-router-dom'
import { errorMessage, isApiError } from '@/api/client'
import type { OutreachDraft, OutreachOptions, OutreachPreview, OutreachResult } from '@/api/growthSignups'
import { MERGE_FIELDS } from '@/api/growthCampaignTypes'
import { Button, Card, CardHeader, ConfirmDialog, FormField, InlineAlert, RadioCards, SelectInput, Textarea, TextInput, useToast } from '@/components/ui'
import { formatDateTime, toDateInput, viewerTimeZone } from '@/lib/format'
import { useDebouncedValue } from '@/lib/hooks'
import { useQuery } from '@/lib/query'
import { newRequestId } from '@/lib/signups'

interface Draft {
  template_id: number | null
  signer: string
  subject: string
  body: string
}

/** Who an email goes to, and the routes that preview and send it. */
export interface OutreachTarget {
  /** The address, for the Send dialog; null when there's none. */
  to: string | null
  /** Known up front (a signup's profile); otherwise the preview says. */
  suppressed?: boolean
  /** What to say when `to` is null. */
  noAddress: string
  /** The preview's query key (the draft is added); it changes with the recipient. */
  previewKey: readonly unknown[]
  preview: (draft: OutreachDraft, signal?: AbortSignal) => Promise<OutreachPreview>
  send: (draft: OutreachDraft & { request_id: string }) => Promise<OutreachResult>
  /** After a send, or a failed one that was recorded: refresh what lists it. */
  onSent: () => void
  /** Offer Schedule beside Send now (only an email to a lead can be scheduled). */
  canSchedule?: boolean
  /** Why nothing can go to them, said in place of a working Send (a lead's: not in the US, on the do-not-contact list, ...). */
  problem?: string | null
  /** The hint under the body, when it isn't the one for a signup or a tenant. */
  fieldsHint?: string
  /** The panel's heading and the line under it. */
  title?: string
  description?: string
  /** Where the draft starts in place of the first template (a reply: "Re: …" and a blank body). A template picked then fills only the body. */
  start?: { subject: string; body: string }
}

const SIGNUP_FIELDS_HINT = `Merge fields: ${MERGE_FIELDS.map((f) => `{{${f}}}`).join(', ')}. A lead's fields (city, state, website, preview_url) have no value here. A fallback fills a missing value: {{first_name|there}}. The signature is added under it.`

/** Tomorrow at 09:00 in the viewer's zone: where Schedule starts. */
function tomorrowMorning(): { date: string; time: string } {
  const d = new Date()
  d.setDate(d.getDate() + 1)
  return { date: toDateInput(d), time: '09:00' }
}

/** The date and time inputs, read in the viewer's zone, as an instant; null when they don't make one. */
function sendAtOf(date: string, time: string): string | null {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(date) || !/^\d{2}:\d{2}$/.test(time)) return null
  const at = new Date(`${date}T${time}`)
  return Number.isNaN(at.getTime()) ? null : at.toISOString()
}

/**
 * `bare` drops the card around it, for a dialog (a lead's Email). Everything else
 * is the same panel.
 */
export function OutreachCompose({ target, options, recipient, bare = false }: { target: OutreachTarget; options: OutreachOptions; recipient?: ReactNode; bare?: boolean }) {
  const toast = useToast()
  const first = target.start ? null : (options.templates[0] ?? null)
  const [draft, setDraft] = useState<Draft>({
    template_id: first?.id ?? null,
    signer: options.signers[0]?.key ?? '',
    subject: target.start?.subject ?? first?.subject ?? '',
    body: target.start?.body ?? first?.body ?? '',
  })
  const [confirming, setConfirming] = useState(false)
  const [when, setWhen] = useState<'now' | 'later'>('now')
  const [later, setLater] = useState(tomorrowMorning)
  const scheduling = !!target.canSchedule && when === 'later'
  const sendAt = scheduling ? sendAtOf(later.date, later.time) : null
  const sendAtProblem = scheduling ? (!sendAt ? 'Pick a date and a time.' : new Date(sendAt).getTime() < Date.now() + 60_000 ? 'Pick a time at least a minute from now.' : null) : null
  const [result, setResult] = useState<OutreachResult | null>(null)
  const [problem, setProblem] = useState<{ message: string; field?: string } | null>(null)
  const set = (patch: Partial<Draft>) => setDraft((d) => ({ ...d, ...patch }))

  const pickTemplate = (id: string) => {
    const t = options.templates.find((x) => String(x.id) === id)
    if (!t) return set({ template_id: null })
    // A reply keeps its own subject unless it was cleared.
    set({ template_id: t.id, subject: target.start && draft.subject.trim() ? draft.subject : t.subject, body: t.body })
  }

  const debounced = useDebouncedValue(JSON.stringify(draft), 300)
  const parsed = useMemo(() => JSON.parse(debounced) as Draft, [debounced])
  const ready = !!parsed.subject.trim() && !!parsed.body.trim() && !!parsed.signer
  const preview = useQuery(ready && target.to ? [...target.previewKey, debounced] : null, (signal) => target.preview(parsed, signal), { keepPrevious: true })
  useEffect(() => setProblem(null), [debounced, target.to])

  const sending = options.sending
  const p = preview.data
  const suppressed = target.suppressed ?? (!!p && p.to === target.to && p.suppressed)
  const problemText = target.problem ?? (p?.problem && p.problem.code !== 'suppressed' ? p.problem.message : null)
  const blocked = suppressed || !target.to || !!problemText || (sending.mode !== 'dry_run' && !sending.smtp_ready)
  const cantFill = p ? p.missing.length > 0 || p.unknown_fields.length > 0 || !!p.subject_problem : false

  const send = async () => {
    setProblem(null)
    try {
      const out = await target.send({ ...draft, ...(scheduling ? { send_at: sendAt } : {}), request_id: newRequestId() })
      setResult(out)
      if (out.outcome === 'sent') toast.success(out.message)
      if (out.outcome === 'scheduled') toast.success(`Scheduled for ${formatDateTime(out.email.scheduled_for)}.`)
      target.onSent()
    } catch (err) {
      setConfirming(false)
      if (isApiError(err) && err.code === 'send_failed') target.onSent()
      setProblem({ message: errorMessage(err), field: isApiError(err) ? err.field : undefined })
    }
  }

  const signerName = options.signers.find((s) => s.key === draft.signer)?.name ?? draft.signer
  const body = (
    <>
      {bare ? null : (
        <CardHeader
          title={target.title ?? 'Write to them'}
          description={target.description ?? 'A 1:1 email from ScoreLM <contact@scorelm.com>, signed by one of the team. Replies come back to that mailbox and show here.'}
        />
      )}
      <div className="grid gap-4">
        {sending.mode === 'dry_run' ? (
          <InlineAlert tone="warn" title="Sending is off on this server">
            Send records the email as a dry run, and nothing leaves. GROWTH_SENDING_ENABLED isn't true on the backend.
          </InlineAlert>
        ) : sending.mode === 'test_list' || !sending.smtp_ready ? (
          <InlineAlert tone="warn">{sending.message}</InlineAlert>
        ) : null}
        {recipient}
        {suppressed ? <InlineAlert tone="bad" title="On the do-not-contact list">Nothing goes to {target.to}.</InlineAlert> : null}
        {!target.to ? <InlineAlert tone="bad">{target.noAddress}</InlineAlert> : null}
        {target.to && !suppressed && problemText ? <InlineAlert tone="bad" title="This can't be sent">{problemText}</InlineAlert> : null}

        <div className="grid gap-3 sm:grid-cols-2">
          <FormField label="Template">
            {({ id }) => (
              <SelectInput
                id={id}
                value={draft.template_id === null ? '' : String(draft.template_id)}
                options={[{ value: '', label: 'No template' }, ...options.templates.map((t) => ({ value: String(t.id), label: t.name }))]}
                onChange={(e) => pickTemplate(e.target.value)}
              />
            )}
          </FormField>
          <FormField label="Signed by">
            {({ id }) => (
              <SelectInput id={id} value={draft.signer} options={options.signers.map((s) => ({ value: s.key, label: s.title ? `${s.name}, ${s.title}` : s.name }))} onChange={(e) => set({ signer: e.target.value })} />
            )}
          </FormField>
        </div>
        <FormField label="Subject" error={problem?.field === 'subject' ? problem.message : null}>
          {({ id, invalid }) => <TextInput id={id} value={draft.subject} maxLength={200} invalid={invalid} onChange={(e) => set({ subject: e.target.value })} />}
        </FormField>
        <FormField
          label="Body"
          hint={target.fieldsHint ?? SIGNUP_FIELDS_HINT}
          error={problem?.field === 'body' ? problem.message : null}
        >
          {({ id, invalid }) => <Textarea id={id} value={draft.body} rows={10} maxLength={20000} invalid={invalid} onChange={(e) => set({ body: e.target.value })} />}
        </FormField>

        <div className="grid gap-2 rounded-md border border-line p-3">
          <p className="text-2xs font-bold uppercase tracking-eyebrow text-muted">Preview</p>
          {p ? (
            <>
              <p className="break-all text-xs text-muted">
                To {p.to} · From {p.from}
              </p>
              <p className="break-words text-[13.5px] font-semibold text-ink" data-testid="outreach-preview-subject">
                {p.subject}
              </p>
              {p.missing.length ? <p className="text-xs text-warn">No value for {p.missing.map((f) => `{{${f}}}`).join(', ')}. Give it a fallback, or take it out.</p> : null}
              {p.unknown_fields.length ? <p className="text-xs text-warn">We can't fill {p.unknown_fields.map((f) => `{{${f}}}`).join(', ')}.</p> : null}
              {p.subject_problem ? <p className="text-xs text-warn">{p.subject_problem}</p> : null}
              <pre data-testid="outreach-preview-text" className="max-w-full overflow-x-hidden whitespace-pre-wrap break-words font-sans text-[13px] text-ink">
                {p.text}
              </pre>
              {/* The logo every one-off email ends with (merge.ts › EMAIL_LOGO_HTML); the text part has none. */}
              <img src="https://scorelm.com/logo.png" width={200} alt="ScoreLM" data-testid="outreach-preview-logo" className="block h-auto" />
            </>
          ) : preview.status === 'error' ? (
            <p className="text-xs text-bad">{errorMessage(preview.error)}</p>
          ) : (
            <p className="text-[13px] text-muted">{ready ? 'Rendering…' : 'Write a subject and a body to see the email.'}</p>
          )}
        </div>

        {target.canSchedule ? (
          <div className="grid gap-3">
            <RadioCards
              legend="When"
              name="outreach-when"
              columns={2}
              value={when}
              onChange={setWhen}
              options={[
                { value: 'now', label: 'Send now', description: 'It goes as soon as you confirm.' },
                { value: 'later', label: 'Schedule', description: 'Pick a day and a time. It waits on the Schedule until then.' },
              ]}
            />
            {scheduling ? (
              <div className="grid gap-3 sm:grid-cols-2">
                <FormField label="Day" error={problem?.field === 'send_at' ? problem.message : null}>
                  {({ id, invalid }) => <TextInput id={id} type="date" value={later.date} invalid={invalid} onChange={(e) => setLater((l) => ({ ...l, date: e.target.value }))} />}
                </FormField>
                <FormField label="Time" hint={`Your time (${viewerTimeZone()}).`}>
                  {({ id }) => <TextInput id={id} type="time" value={later.time} onChange={(e) => setLater((l) => ({ ...l, time: e.target.value }))} />}
                </FormField>
                {sendAtProblem ? <p className="text-xs text-warn sm:col-span-2">{sendAtProblem}</p> : null}
              </div>
            ) : null}
          </div>
        ) : null}

        {problem && !problem.field ? <InlineAlert tone="bad">{problem.message}</InlineAlert> : null}
        {result ? (
          <InlineAlert
            tone={result.outcome === 'sent' || result.outcome === 'scheduled' ? 'good' : 'warn'}
            title={result.outcome === 'sent' ? 'Sent' : result.outcome === 'scheduled' ? 'Scheduled' : result.outcome === 'dry_run' ? 'Dry run: nothing was sent' : 'Not sent'}
          >
            {result.message}
            {result.outcome === 'scheduled' ? (
              <>
                {' '}
                <Link to="/growth/schedule" className="underline underline-offset-2 hover:text-accent">
                  Open the Schedule
                </Link>
              </>
            ) : null}
          </InlineAlert>
        ) : null}

        <div className="flex justify-end">
          <Button variant="primary" disabled={blocked || !ready || cantFill || !!sendAtProblem || preview.status === 'loading'} onClick={() => { setResult(null); setConfirming(true) }}>
            {scheduling ? 'Schedule' : sending.mode === 'dry_run' ? 'Send (dry run)' : 'Send'}
          </Button>
        </div>
      </div>

      <ConfirmDialog
        open={confirming}
        onClose={() => setConfirming(false)}
        title={scheduling ? `Schedule this email to ${target.to}?` : sending.mode === 'dry_run' ? 'Record a dry run?' : `Send this email to ${target.to}?`}
        description={
          scheduling
            ? `It goes on ${formatDateTime(sendAt)}, from ScoreLM <contact@scorelm.com>, signed by ${signerName}. Until then it's on the Schedule, where you can remove or move it.${sending.mode === 'dry_run' ? ' Sending is off on this server, so at that time it will be recorded as a dry run and nothing will leave.' : ''}`
            : sending.mode === 'dry_run'
              ? "Sending is off on this server, so this is recorded as a dry run and nothing leaves. It shows on the timeline as one."
              : `It goes now, from ScoreLM <contact@scorelm.com>, signed by ${signerName}. It can't be unsent.`
        }
        confirmLabel={scheduling ? 'Schedule email' : sending.mode === 'dry_run' ? 'Record dry run' : 'Send email'}
        tone="primary"
        onConfirm={send}
      />
    </>
  )
  return bare ? <div>{body}</div> : <Card>{body}</Card>
}
